/**
 * Utility xử lý URL theo subpath deployment
 * Khi ứng dụng deploy tại subpath (VD: /accounting), các URL tuyệt đối phải
 * được prefix bằng BASE_URL để tránh điều hướng ra ngoài subpath.
 *
 * Sử dụng window.BASE_URL — được set trong public/config/env.js (runtime),
 * override bởi Helm ConfigMap khi deploy. Mặc định '/' (deploy tại root).
 *
 * Created by: ncphuoc - 2026
 */

/**
 * Trả về base path của ứng dụng, không có trailing slash.
 * VD: '/accounting' hoặc '' (khi deploy tại root)
 * @returns {string}
 */
export const getAppBase = () => {
  return (window.BASE_URL ?? '/').replace(/\/$/, '');
};

/**
 * Tạo URL đầy đủ bằng cách prefix base path vào path tương đối.
 * Dùng cho window.location.pathname, window.open, và các URL không qua vue-router.
 *
 * VD: buildAppUrl('/timesheet/management-request/attendance-watch')
 *     → '/accounting/timesheet/management-request/attendance-watch'
 *     → '/timesheet/...' (khi deploy tại root)
 *
 * @param {string} path - Đường dẫn tương đối, bắt đầu bằng '/'
 * @returns {string}
 */
export const buildAppUrl = (path) => {
  const base = getAppBase();
  // Tránh double slash khi base rỗng
  return base + (path.startsWith('/') ? path : '/' + path);
};

/**
 * Tạo URL tuyệt đối (full origin + base + path).
 * Dùng cho window.open, window.location.href khi cần full URL.
 *
 * VD: buildFullUrl('/404')
 *     → 'https://host/accounting/404'
 *
 * @param {string} path - Đường dẫn tương đối, bắt đầu bằng '/'
 * @returns {string}
 */
export const buildFullUrl = (path) => {
  return window.location.origin + buildAppUrl(path);
};

/**
 * Lấy pathname hiện tại đã loại bỏ base prefix.
 * Dùng để parse các segment của path (subsystem, tab...) mà không bị ảnh hưởng bởi base.
 *
 * VD: khi URL là '/accounting/treasury/voucher'
 *     → trả về '/treasury/voucher'
 *     Khi deploy tại root với URL '/treasury/voucher'
 *     → trả về '/treasury/voucher'
 *
 * @returns {string}
 */
export const getRoutePath = () => {
  const base = getAppBase();
  return window.location.pathname.slice(base.length) || '/';
};

export default {
  getAppBase,
  buildAppUrl,
  buildFullUrl,
  getRoutePath,
};
